import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { genStyles } from '../converter-layout/generator-layout';
import styles from './roman-numeral-converter.component.module.scss';

interface State {
    decimalInput: string;
    romanInput: string;
    copiedField: 'decimal' | 'roman' | null;
}

const NUMERALS: [number, string][] = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
];

const ROMAN_VAL: Record<string, number> = {
    I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000,
};

export default class RomanNumeralConverterComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = { decimalInput: '2026', romanInput: '', copiedField: null };

    componentDidMount() {
        this.setState({ romanInput: this.toRoman(2026) });
    }

    private toRoman(n: number): string {
        if (!Number.isInteger(n) || n < 1 || n > 3999) return '';
        let result = '';
        let remaining = n;
        for (const [val, sym] of NUMERALS) {
            while (remaining >= val) {
                result += sym;
                remaining -= val;
            }
        }
        return result;
    }

    private fromRoman(roman: string): number | null {
        const r = roman.trim().toUpperCase();
        if (!r || !/^[MDCLXVI]+$/.test(r)) return null;
        let total = 0;
        for (let i = 0; i < r.length; i++) {
            const cur = ROMAN_VAL[r[i]];
            const next = i + 1 < r.length ? ROMAN_VAL[r[i + 1]] : 0;
            if (cur < next) total -= cur;
            else total += cur;
        }
        // Round-trip check to validate canonical form
        if (this.toRoman(total) !== r) return null;
        return total;
    }

    onDecimalChange = (val: string) => {
        const n = parseInt(val, 10);
        if (!val.trim()) {
            this.setState({ decimalInput: '', romanInput: '', copiedField: null });
            return;
        }
        if (Number.isInteger(n) && n >= 1 && n <= 3999) {
            this.setState({ decimalInput: val, romanInput: this.toRoman(n), copiedField: null });
        } else {
            this.setState({ decimalInput: val, romanInput: 'Out of range (1–3,999)', copiedField: null });
        }
    };

    onRomanChange = (val: string) => {
        if (!val.trim()) {
            this.setState({ romanInput: '', decimalInput: '', copiedField: null });
            return;
        }
        const n = this.fromRoman(val);
        if (n !== null) {
            this.setState({ romanInput: val.toUpperCase(), decimalInput: String(n), copiedField: null });
        } else {
            this.setState({ romanInput: val.toUpperCase(), decimalInput: 'Invalid Roman numeral', copiedField: null });
        }
    };

    copyField = (field: 'decimal' | 'roman') => {
        const val = field === 'decimal' ? this.state.decimalInput : this.state.romanInput;
        if (!val || val.includes('Invalid') || val.includes('Out of range')) return;
        navigator.clipboard.writeText(val).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: field });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    render() {
        const { decimalInput, romanInput, copiedField } = this.state;
        const decValid = !decimalInput.includes('Invalid') && !decimalInput.includes('Out of range');
        const romanValid = !romanInput.includes('Invalid') && !romanInput.includes('Out of range');
        return (
            <div className="row">
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>
                            Decimal (1–3,999)
                            <input className={`${styles.input} ${!decValid ? styles.inputError : ''}`}
                                type="text" value={decimalInput}
                                onChange={e => this.onDecimalChange(e.target.value)} />
                        </label>
                        <button className={`${sharedStyles.copyButton} ${copiedField === 'decimal' ? sharedStyles.copyButtonSuccess : ''}`}
                            onClick={() => this.copyField('decimal')} disabled={!decValid || !decimalInput}>
                            {copiedField === 'decimal' ? '✓ Copied' : 'Copy decimal'}
                        </button>
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>
                            Roman numeral
                            <input className={`${styles.input} ${styles.romanFont} ${!romanValid ? styles.inputError : ''}`}
                                type="text" value={romanInput}
                                onChange={e => this.onRomanChange(e.target.value)} />
                        </label>
                        <button className={`${sharedStyles.copyButton} ${copiedField === 'roman' ? sharedStyles.copyButtonSuccess : ''}`}
                            onClick={() => this.copyField('roman')} disabled={!romanValid || !romanInput}>
                            {copiedField === 'roman' ? '✓ Copied' : 'Copy Roman'}
                        </button>
                    </div>
                </div>
                <div className="col-12">
                    <div className={genStyles.section}>
                        <p className={styles.note}>
                            Standard subtractive notation (e.g., IV = 4, IX = 9, XL = 40). No "vinculum overline" form for numbers above 3,999.
                        </p>
                    </div>
                </div>
            </div>
        );
    }
}
